<template>
    <div
        class="px-4 py-12 sm:px-6 flex min-h-full flex-col items-center justify-center"
    >
        <div class="max-w-lg w-full">
            <div class="bg-white shadow rounded-lg overflow-hidden">
                <template v-if="eventAttendee.status !== 'canceled'">
                    <div class="p-6 sm:p-8 space-y-6">
                        <h1 class="text-xl font-semibold text-iron-900">
                            {{ eventAttendee.event_title }}
                        </h1>

                        <div
                            class="gap-4 bg-iron-50 p-4 rounded-lg flex items-center"
                        >
                            <div
                                class="h-12 w-12 bg-white text-z-primary-600 shadow-sm rounded-lg flex shrink-0 items-center justify-center"
                            >
                                <CalendarDays :size="24" />
                            </div>
                            <div>
                                <p
                                    class="font-semibold text-iron-900 first-letter:uppercase"
                                >
                                    {{ format(start, "PPPP") }}
                                </p>
                                <p class="text-iron-600">
                                    {{ format(start, "H:mm") }} –
                                    {{ format(end, "H:mm") }}
                                    <span class="text-iron-400">·</span>
                                    {{ timeZone }}
                                </p>
                            </div>
                        </div>

                        <dl class="gap-4 grid">
                            <div class="gap-3 flex items-start">
                                <UserRound
                                    :size="18"
                                    class="mt-0.5 text-iron-400 shrink-0"
                                />
                                <div class="min-w-0">
                                    <dt class="text-sm text-iron-500">
                                        {{ t("cancel_page.organized_by") }}
                                    </dt>
                                    <dd
                                        class="font-medium text-iron-900 break-words"
                                    >
                                        {{ eventAttendee.organizer }}
                                    </dd>
                                </div>
                            </div>

                            <div class="gap-3 flex items-start">
                                <Mail
                                    :size="18"
                                    class="mt-0.5 text-iron-400 shrink-0"
                                />
                                <div class="min-w-0">
                                    <dt class="text-sm text-iron-500">
                                        {{ t("cancel_page.scheduled_by") }}
                                    </dt>
                                    <dd
                                        class="font-medium text-iron-900 break-words"
                                    >
                                        {{ eventAttendee.email }}
                                    </dd>
                                </div>
                            </div>

                            <div
                                v-if="eventAttendee.meetingLink"
                                class="gap-3 flex items-start"
                            >
                                <MapPin
                                    :size="18"
                                    class="mt-0.5 text-iron-400 shrink-0"
                                />
                                <div class="min-w-0">
                                    <dt class="text-sm text-iron-500">
                                        {{ t("cancel_page.meet_link") }}
                                    </dt>
                                    <dd>
                                        <a
                                            :href="eventAttendee.meetingLink"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            class="gap-2 font-medium text-z-primary-600 hover:text-z-primary-700 flex items-center"
                                        >
                                            <span
                                                v-if="location"
                                                class="h-5 w-5 shrink-0"
                                                v-html="location.icon"
                                            ></span>
                                            {{
                                                location?.name ??
                                                eventAttendee.meetingLink
                                            }}
                                            <ExternalLink
                                                :size="16"
                                                class="shrink-0"
                                            />
                                        </a>
                                    </dd>
                                </div>
                            </div>
                        </dl>
                    </div>

                    <div
                        class="gap-3 border-iron-100 p-6 sm:px-8 flex flex-col border-t"
                    >
                        <div
                            class="gap-3 grid"
                            :class="{ 'sm:grid-cols-2': canReschedule }"
                        >
                            <DropdownMenu>
                                <DropdownMenuTrigger as-child>
                                    <Button
                                        variant="outline"
                                        class="gap-2 flex w-full"
                                    >
                                        <CalendarPlus :size="18" />
                                        {{ t("cancel_page.add_to_calendar") }}
                                        <ChevronDown
                                            :size="16"
                                            class="text-iron-400"
                                        />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    align="start"
                                    class="min-w-[--radix-dropdown-menu-trigger-width]"
                                >
                                    <DropdownMenuItem
                                        v-for="target in calendarTargets"
                                        :key="target.name"
                                        as-child
                                    >
                                        <a
                                            class="gap-x-2 flex items-center"
                                            :href="target.href"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <img
                                                v-if="target.id !== 'ics'"
                                                :src="calendarLogos[target.id]"
                                                alt=""
                                                class="h-4 w-4 shrink-0"
                                            />
                                            <CalendarArrowDown
                                                v-else
                                                :size="16"
                                                class="shrink-0"
                                            />
                                            {{ target.name }}
                                        </a>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>

                            <Button
                                v-if="canReschedule"
                                as="a"
                                :href="rescheduleLink"
                                target="_blank"
                                variant="outline"
                                class="gap-2 flex w-full"
                            >
                                <CalendarClock :size="18" />
                                {{ t("cancel_page.reschedule") }}
                            </Button>
                        </div>

                        <Button
                            v-if="canCancel"
                            variant="destructive-ghost"
                            class="w-full"
                            :disabled="loading"
                            @click="cancel"
                        >
                            {{ t("cancel_page.cancel") }}
                        </Button>

                        <p
                            v-if="restrictionNote"
                            class="gap-2 text-sm text-iron-500 flex items-center justify-center"
                        >
                            <Info :size="16" class="shrink-0" />
                            {{ restrictionNote }}
                        </p>
                    </div>
                </template>

                <div
                    v-else
                    class="gap-3 p-8 flex flex-col items-center text-center"
                >
                    <div
                        class="h-12 w-12 bg-iron-50 text-iron-500 flex items-center justify-center rounded-full"
                    >
                        <CalendarX :size="24" />
                    </div>
                    <h1 class="text-lg font-semibold text-iron-900">
                        {{ t("cancel_page.canceled") }}
                    </h1>
                    <p class="text-iron-500">
                        {{ eventAttendee.event_title }} ·
                        <span class="first-letter:uppercase">{{
                            format(start, "PPPP")
                        }}</span>
                    </p>
                </div>
            </div>

            <PoweredByZaptime v-if="showBranding" class="mt-6" />
        </div>

        <ConfirmDialog></ConfirmDialog>
    </div>
</template>

<script lang="ts" setup>
import axios from "axios";
import { useI18n } from "vue-i18n";
import { computed, ref, type PropType } from "vue";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useLocalisedTzFormat } from "@/composables/useLocalisedTzFormat";
import {
    CalendarArrowDown,
    CalendarDays,
    CalendarPlus,
    CalendarClock,
    CalendarX,
    ChevronDown,
    ExternalLink,
    Info,
    Mail,
    MapPin,
    UserRound,
} from "lucide-vue-next";
import { useLocationOptions } from "../EventTypes/useLocationOptions";
import {
    getCalendarTargets,
    type CalendarLinks,
} from "./utils/getCalendarTargets";
import GoogleCalendarLogo from "../Integrations/components/logos/simple/GoogleCalendar.svg?url";
import MicrosoftLogo from "../Integrations/components/logos/simple/Microsoft.svg?url";
import OutlookLogo from "../Integrations/components/logos/simple/Outlook.svg?url";
import YahooLogo from "../Integrations/components/logos/simple/Yahoo.svg?url";
import useConfirmDialog from "../ConfirmDialog/composables/useConfirm";
import ConfirmDialog from "../ConfirmDialog/components/ConfirmDialog.vue";
import PoweredByZaptime from "./components/PoweredByZaptime.vue";
import { posthog } from "posthog-js";

const { getLocationForId } = useLocationOptions();
const { show } = useConfirmDialog();

const props = defineProps({
    locale: {
        type: String,
        required: true,
    },
    eventAttendee: {
        type: Object,
        required: true,
    },
    cancelLink: {
        type: String,
        required: true,
    },
    downloadLink: {
        type: String,
        required: true,
    },
    calendarLinks: {
        type: Object as PropType<CalendarLinks | null>,
        default: null,
    },
    rescheduleLink: {
        type: String,
        required: true,
    },
    canReschedule: {
        type: Boolean,
        default: true,
    },
    canCancel: {
        type: Boolean,
        default: true,
    },
    showBranding: {
        type: Boolean,
        default: false,
    },
});

const { locale, t } = useI18n();

locale.value = props.locale;

const { format } = useLocalisedTzFormat();
const loading = ref(false);

const start = computed(() => new Date(props.eventAttendee.start));
const end = computed(() => new Date(props.eventAttendee.end));
const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const location = computed(() =>
    props.eventAttendee.location
        ? getLocationForId(props.eventAttendee.location.type)
        : undefined,
);

const restrictionNote = computed(() => {
    if (!props.canReschedule && !props.canCancel) {
        return t("cancel_page.changes_not_allowed");
    }

    if (!props.canReschedule) {
        return t("cancel_page.reschedule_not_allowed");
    }

    if (!props.canCancel) {
        return t("cancel_page.cancel_not_allowed");
    }

    return null;
});

const calendarLogos: Record<keyof CalendarLinks, string> = {
    google: GoogleCalendarLogo,
    outlook: OutlookLogo,
    office365: MicrosoftLogo,
    yahoo: YahooLogo,
};

const calendarTargets = computed(() =>
    getCalendarTargets(props.calendarLinks, props.downloadLink),
);

async function cancel() {
    const ok = await show({
        header: t("cancel_page.cancel_dialog_title"),
        message: t("cancel_page.really_want_to_cancel"),
        cancel: t("close"),
        confirm: t("cancel_page.cancel"),
    });

    if (!ok) {
        return;
    }

    loading.value = true;

    axios
        .post(props.cancelLink)
        .then(() => {
            posthog.capture("booking_cancelled", {
                event_title: props.eventAttendee.event_title,
                organizer: props.eventAttendee.organizer,
            });
            window.location.reload();
        })
        .catch(() => {
            loading.value = false;
        });
}
</script>
